/**
 * AgentChatWidget — Self-contained embeddable chat widget
 * 
 * Contains the complete agent experience:
 * - Agent sidebar with selection
 * - Chat with streaming responses
 * - Voice mode (if agent has voice capability)
 * - OCR document upload (if agent has ocr capability)
 * - Token tracking
 * 
 * Can be embedded in:
 * - The Launchpad Chat Interface (ChatPage)
 * - Deployed client apps
 * - Any external website via iframe or React import
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Loader2, Upload, FileText, CheckCircle, XCircle, ScanText,
  Trash2, Mic, MicOff, Zap, Eye, Volume2,
} from 'lucide-react';
import ChatMessage from '../Chat/ChatMessage';
import GeminiVoiceChat from '../Voice/GeminiVoiceChat';
import TemplateCommandPopup from '../Chat/TemplateCommandPopup';
import { Agent, Message } from '../../types';
import toast from 'react-hot-toast';

export interface AgentChatWidgetProps {
  /** Core API base URL */
  apiUrl?: string;
  /** Pre-loaded agents list (if not provided, fetches from API) */
  agents?: Agent[];
  /** Pre-selected agent */
  initialAgentId?: string;
  /** User ID for billing */
  userId?: string;
  /** Role filter for agent loading */
  role?: string;
  /** Show the agent sidebar */
  showSidebar?: boolean;
  /** Show the header bar */
  showHeader?: boolean;
  /** Custom title */
  title?: string;
  /** Height of the widget */
  height?: string;
  /** Called when token balance changes */
  onTokenUpdate?: (tokens: { session: number; remaining: number | null }) => void;
  /** Database context provider — called before each query to get client/case data */
  getDbContext?: () => string | null;
  /** Active case id — when set, the primary agent receives this case's micro-agent data */
  caseId?: string;
  /** Dynamic active-case provider — called before each query (takes precedence over caseId prop) */
  getCaseId?: () => string | null;
  /** Called after each complete AI response with query + response text.
   *  attachedFiles (if any) are passed so the parent can promote them to a case on link. */
  onMessageComplete?: (query: string, response: string, files?: Array<{ mimeType: string; data: string; name: string }>) => void;
}

const CAPABILITY_ICONS: Record<string, string> = {
  ocr: '📄', voice: '🎤', legal: '⚖️', 'legal-analysis': '📊',
  'document-review': '📋', 'case-research': '🔍', 'contract-drafting': '✍️',
  multilingual: '🌐', 'indian-law': '🇮🇳', chat: '💬',
};

// Rotating loading messages — professional + fun Hindi filmy dialogues
const LOADING_MESSAGES = [
  'Researching your query...',
  'Analyzing legal provisions...',
  'Tarikh pe tarikh... but we deliver faster! ⚖️',
  'Consulting the law books...',
  'Aaj ka kanoon kehta hai... wait karo! 📚',
  'Preparing your answer...',
  'Kanoon ke haath lambe hote hain... aur hamare bhi! 🤝',
  'Cross-referencing case laws...',
  'Objection overruled! Answer loading... 🔨',
  'Drafting response with precision...',
  'Insaaf ki talwar tez ho rahi hai... ⚔️',
  'Reviewing statutory provisions...',
  'Milord, ek minute... jawab tayyar ho raha hai! 👨‍⚖️',
  'Checking latest amendments...',
  'Dosti ka ek usool hai... query ka jawab dena! 💪',
];

const LoadingMessage: React.FC = () => {
  const [msgIndex, setMsgIndex] = useState(0);
  useEffect(() => {
    const initial = Math.floor(Math.random() * LOADING_MESSAGES.length);
    setMsgIndex(initial);
    const interval = setInterval(() => {
      setMsgIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  return <p className="text-[10px] text-gray-400 mt-0.5 italic animate-pulse">{LOADING_MESSAGES[msgIndex]}</p>;
};

const AgentChatWidget: React.FC<AgentChatWidgetProps> = ({
  apiUrl = (window as any).__CONFIG__?.agents ? '' : (process.env.REACT_APP_CORE_API || 'https://vidhiguruapi.vidhiguru.in'),
  agents: externalAgents,
  initialAgentId,
  userId = (window as any).__CONFIG__?.clientId || '9223037474',
  role = 'lawyer',
  showSidebar = true,
  showHeader = true,
  title,
  height = '100%',
  onTokenUpdate,
  getDbContext,
  caseId,
  getCaseId,
  onMessageComplete,
}) => {
  // Agents
  const [agents, setAgents] = useState<Agent[]>(externalAgents || []);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  // Messages
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Persist the session id so conversation memory survives page refreshes.
  // Read once from localStorage; only generate a new id when none exists. This
  // prevents a fresh id (and thus lost memory) on every render or when storage
  // is briefly unavailable.
  const sessionIdRef = useRef<string>('');
  if (!sessionIdRef.current) {
    let existing: string | null = null;
    try { existing = typeof window !== 'undefined' ? window.localStorage.getItem('chat_session_id') : null; } catch { /* storage blocked */ }
    sessionIdRef.current = existing || `session-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try { window.localStorage.setItem('chat_session_id', sessionIdRef.current); } catch { /* ignore */ }
  }
  // True only inside a deployed/standalone client app (has baked-in config).
  const isStandalone = typeof window !== 'undefined' && !!(window as any).__CONFIG__?.agents;

  // OCR
  const [ocrProcessing, setOcrProcessing] = useState(false);
  const [ocrText, setOcrText] = useState<string | null>(null);
  const [ocrConfirmed, setOcrConfirmed] = useState(false);
  const [ocrPreview, setOcrPreview] = useState<string | null>(null);
  const [ocrAnalysis, setOcrAnalysis] = useState<any>(null);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrCurrentPage, setOcrCurrentPage] = useState(0);
  const [ocrTotalPages, setOcrTotalPages] = useState(0);
  const [ocrSnippet, setOcrSnippet] = useState('');
  const [ocrStartTime, setOcrStartTime] = useState(0);
  const [ocrEstTime, setOcrEstTime] = useState('');
  const [ocrPdfFile, setOcrPdfFile] = useState<string | null>(null); // base64 for PDF preview
  const fileInputRef = useRef<HTMLInputElement>(null);

  // DocAI — files attached for native multimodal processing (replaces OCR flow)
  const [attachedFiles, setAttachedFiles] = useState<Array<{ mimeType: string; data: string; name: string; preview?: string }>>([]);
  const [useDocAI, setUseDocAI] = useState(true); // DocAI mode ON by default (skips OCR)

  // Voice
  const [voiceActive, setVoiceActive] = useState(false);
  const voiceAgentMsgIdRef = useRef<string | null>(null);
  const voiceAgentTextRef = useRef('');
  const voiceUserTextRef = useRef('');

  // Template command
  const [showTemplatePopup, setShowTemplatePopup] = useState(false);
  const [templateFilterText, setTemplateFilterText] = useState('');

  // Tokens
  const [sessionTokens, setSessionTokens] = useState(0);
  const [tokensRemaining, setTokensRemaining] = useState<number | null>(null);
  const [lastQueryTokens, setLastQueryTokens] = useState(0);

  const hasOcr = (selectedAgent?.capabilities || []).some(c => c.toLowerCase().includes('ocr'));
  const hasVoice = (selectedAgent?.capabilities || []).some(c => c.toLowerCase().includes('voice'));

  // ── Init ──
  useEffect(() => {
    if (!externalAgents) loadAgents();
    loadTokenBalance();
  }, []);

  // Restore the visible conversation after a page/PWA reload (standalone apps only),
  // so a refresh or backgrounded tab doesn't wipe the thread the user was in.
  useEffect(() => {
    if (!isStandalone) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/conversations/session/${encodeURIComponent(sessionIdRef.current)}`);
        const data = await res.json();
        const rows: Array<{ role: string; content: string; timestamp?: string }> = data?.messages || [];
        if (cancelled || rows.length === 0) return;
        setMessages(prev => {
          if (prev.length > 0) return prev; // don't clobber an active thread
          return rows.map((r, i) => ({
            id: `restored-${i}`,
            role: r.role === 'assistant' ? 'assistant' : 'user',
            content: r.content || '',
            timestamp: r.timestamp ? new Date(r.timestamp) : new Date(),
          })) as Message[];
        });
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [isStandalone, apiUrl]);

  useEffect(() => {
    if (externalAgents) setAgents(externalAgents);
  }, [externalAgents]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    // Auto-select agent when agents list changes or initialAgentId changes
    if (agents.length > 0) {
      const target = initialAgentId ? agents.find(a => a.id === initialAgentId) : agents[0];
      if (target && (!selectedAgent || selectedAgent.id !== target.id)) {
        handleSelectAgent(target);
      }
    }
  }, [agents, initialAgentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    onTokenUpdate?.({ session: sessionTokens, remaining: tokensRemaining });
  }, [sessionTokens, tokensRemaining]);

  const loadAgents = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/agents?role=${role}`);
      const data = await res.json();
      setAgents(data.agents || []);
    } catch { /* silent */ }
  };

  const loadTokenBalance = async () => {
    try {
      // In standalone mode (deployed app), use /api/tokens for client-specific balance
      // In Launchpad mode, use CoreAPI's billing/usage for master pool
      const isStandalone = !!(window as any).__CONFIG__?.agents;
      const tokenUrl = isStandalone ? '/api/tokens' : `${apiUrl}/api/billing/usage`;
      const res = await fetch(tokenUrl);
      const data = await res.json();
      if (isStandalone) {
        setTokensRemaining(data.remaining ?? null);
      } else {
        setTokensRemaining(data.usage?.tokensRemaining ?? null);
      }
    } catch { /* silent */ }
  };

  // Persist a completed turn to the server so the AI's [SESSION MEMORY] populates
  // (the deployed app reads memory from the conversations table). Fire-and-forget.
  const persistTurn = useCallback((userText: string, assistantText: string) => {
    if (!isStandalone) return; // Launchpad mode has its own persistence
    const sessionId = sessionIdRef.current;
    const agentId = selectedAgent?.id || '';
    const turnCaseId = (getCaseId ? getCaseId() : null) || caseId || '';
    const post = (role: 'user' | 'assistant', content: string) => {
      if (!content) return;
      fetch(`${apiUrl}/api/conversations/message`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, role, content, agentId, caseId: turnCaseId, type: 'text' }),
      }).catch(() => { /* non-blocking */ });
    };
    post('user', userText);
    post('assistant', assistantText);
  }, [isStandalone, selectedAgent, apiUrl, getCaseId, caseId]);

  const handleSelectAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setMessages([]);
    setOcrText(null); setOcrConfirmed(false); setOcrPreview(null); setOcrAnalysis(null);
    setVoiceActive(false); setSessionTokens(0); setLastQueryTokens(0);
    voiceAgentMsgIdRef.current = null; voiceAgentTextRef.current = '';
  };

  // ── Voice transcript callbacks ──
  const handleVoiceUserTranscript = useCallback((text: string) => {
    voiceUserTextRef.current = text;
    // Show user speech in chat
    setMessages(prev => {
      const voiceUserMsgs = prev.filter(m => m.id.startsWith('voice-user-'));
      const lastUserVoice = voiceUserMsgs.length > 0 ? voiceUserMsgs[voiceUserMsgs.length - 1] : null;
      if (lastUserVoice && Date.now() - lastUserVoice.timestamp.getTime() < 5000) {
        return prev.map(m => m.id === lastUserVoice.id ? { ...m, content: '🎤 ' + text } : m);
      }
      return [...prev, { id: `voice-user-${Date.now()}`, role: 'user' as const, content: '🎤 ' + text, timestamp: new Date() }];
    });
  }, []);

  const handleVoiceAgentTranscript = useCallback((text: string) => {
    voiceAgentTextRef.current += text;
    const currentText = voiceAgentTextRef.current;
    if (!voiceAgentMsgIdRef.current) {
      const msgId = `voice-${Date.now()}`;
      voiceAgentMsgIdRef.current = msgId;
      setMessages(prev => [...prev, { id: msgId, role: 'assistant', content: '🔊 ' + currentText, timestamp: new Date() }]);
    } else {
      const msgId = voiceAgentMsgIdRef.current;
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, content: '🔊 ' + currentText } : m));
    }
  }, []);

  const handleVoiceTurnComplete = useCallback(() => {
    const agentText = voiceAgentTextRef.current;
    const userText = voiceUserTextRef.current;
    
    // Save voice transcript to backend (SQLite) for memory
    if (agentText && (window as any).__CONFIG__?.agents) {
      const saveUrl = '/api/sessions/answer';
      fetch(saveUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          agentId: selectedAgent?.id || '',
          query: userText ? '[VOICE] ' + userText : '[VOICE INPUT]',
          response: agentText,
          tokensCharged: 0,
        }),
      }).catch(() => {});
    }

    // Notify parent for conversation linking
    if (agentText && onMessageComplete) {
      onMessageComplete(userText ? '[Voice] ' + userText : '[Voice query]', agentText);
    }

    voiceAgentMsgIdRef.current = null;
    voiceAgentTextRef.current = '';
    voiceUserTextRef.current = '';
  }, [selectedAgent, onMessageComplete]);

  // ── DocAI File Attach (Native Multimodal — replaces OCR) ──
  const handleFileUpload = useCallback(async (file: File) => {
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf',
      'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type) && !file.type.startsWith('image/')) {
      toast.error('Supported: images, PDF, DOC/DOCX');
      return;
    }
    if (attachedFiles.length >= 10) {
      toast.error('Maximum 10 files per query');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large (max 20MB per file)');
      return;
    }

    try {
      const base64 = await new Promise<string>((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve((r.result as string).split(',')[1]);
        r.readAsDataURL(file);
      });

      // Generate preview for images
      let preview: string | undefined;
      if (file.type.startsWith('image/')) {
        preview = await new Promise<string>((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result as string);
          r.readAsDataURL(file);
        });
      }

      setAttachedFiles(prev => [...prev, { mimeType: file.type, data: base64, name: file.name, preview }]);
      toast.success(`📎 ${file.name} attached`);
    } catch (err: any) {
      toast.error('Failed to attach file: ' + err.message);
    }
  }, [attachedFiles]);

  const handleRemoveFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClearFiles = () => { setAttachedFiles([]); };

  const handleConfirmOcr = () => {
    setOcrConfirmed(true);
    setMessages(prev => [...prev, {
      id: Date.now().toString(), role: 'assistant',
      content: `📄 Document loaded.\n\nPreview:\n"${(ocrText || '').slice(0, 300)}${(ocrText || '').length > 300 ? '...' : ''}"`,
      timestamp: new Date(),
    }]);
  };

  const handleClearOcr = () => { setOcrText(null); setOcrConfirmed(false); setOcrPreview(null); setOcrAnalysis(null); setOcrPdfFile(null); setAttachedFiles([]); };

  // ── Send message (streaming with non-streaming fallback) ──
  const handleSend = async () => {
    if (!input.trim() || !selectedAgent || loading) return;
    const q = input;
    const userMsgId = 'user-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
    const assistantMsgId = 'bot-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);

    // Show attached file names in user message
    const fileNames = attachedFiles.map(f => f.name).join(', ');
    const userContent = fileNames ? `${q}\n📎 ${fileNames}` : q;

    setMessages(prev => [
      ...prev,
      { id: userMsgId, role: 'user' as const, content: userContent, timestamp: new Date() },
      { id: assistantMsgId, role: 'assistant' as const, content: '', timestamp: new Date() },
    ]);
    setInput(''); setLoading(true); setLastQueryTokens(0);

    const updateMsg = (text: string) => setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, content: text } : m));

    // Resolve the active case id (dynamic provider takes precedence over the prop).
    // When present, the deployed app injects that case's micro-agent data into the query.
    const activeCaseId = (getCaseId ? getCaseId() : null) || caseId || null;

    // ── DocAI path: files attached → use /api/query/docai/stream ──
    if (attachedFiles.length > 0) {
      try {
        const docaiBody: any = {
          userId,
          query: q,
          files: attachedFiles.map(f => ({ mimeType: f.mimeType, data: f.data })),
          agentId: selectedAgent.id,
          ...(activeCaseId ? { caseId: activeCaseId } : {}),
        };

        // Inject database context into query if available
        if (getDbContext) {
          const dbCtx = getDbContext();
          if (dbCtx) {
            docaiBody.query = `[DATABASE CONTEXT]\n${dbCtx}\n[END DATABASE CONTEXT]\n\n${q}`;
          }
        }

        const res = await fetch(`${apiUrl}/api/query/docai/stream`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(docaiBody),
        });

        if (!res.body) throw new Error('No response body');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let full = '';

        // Watchdog: multi-PDF processing can take a while before the FIRST token.
        // Allow a long first-byte window, then a shorter gap between chunks. This
        // prevents both premature give-up AND infinite hangs (tokens charged, no output).
        let gotFirstByte = false;
        let streamTimeout: ReturnType<typeof setTimeout> | null = null;
        const FIRST_BYTE_MS = 120000; // up to 2 min for first token (many PDFs = slow)
        const INTER_CHUNK_MS = 30000;  // 30s between chunks once streaming
        const armWatchdog = () => {
          if (streamTimeout) clearTimeout(streamTimeout);
          streamTimeout = setTimeout(() => { try { reader.cancel(); } catch {} },
            gotFirstByte ? INTER_CHUNK_MS : FIRST_BYTE_MS);
        };
        armWatchdog();

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            armWatchdog();
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            let evt = '';
            for (const line of lines) {
              if (line.startsWith('event: ')) evt = line.slice(7).trim();
              else if (line.startsWith('data: ')) {
                try {
                  const d = JSON.parse(line.slice(6));
                  if (evt === 'chunk' && d.text) {
                    gotFirstByte = true;
                    full += d.text;
                    updateMsg(full);
                  } else if (evt === 'done') {
                    const charged = d?.billing?.tokensCharged || 0;
                    setLastQueryTokens(charged);
                    setSessionTokens(prev => prev + charged);
                    loadTokenBalance();
                  } else if (evt === 'error') {
                    throw new Error(d.error || 'DocAI processing failed');
                  }
                } catch (parseErr: any) {
                  if (parseErr.message.includes('DocAI') || parseErr.message.includes('failed')) throw parseErr;
                }
              }
            }
          }
        } finally {
          if (streamTimeout) clearTimeout(streamTimeout);
        }

        // If the stream ended/cancelled but we DID receive partial text, keep it
        // (don't discard a partial answer the user already paid for).
        if (!full) updateMsg('⏱️ The documents took too long to process and no response arrived. Your tokens for a completed response are not charged for empty output — please retry, ideally with fewer/smaller PDFs.');
        else {
          persistTurn(q, full);
          // Pass the attached files so the parent can promote them to a case on link.
          const filesForLink = attachedFiles.map(f => ({ mimeType: f.mimeType, data: f.data, name: f.name }));
          if (onMessageComplete) onMessageComplete(q, full, filesForLink);
        }

        // Clear attached files after successful query
        setAttachedFiles([]);
      } catch (e: any) {
        const errMsg = e.message || 'DocAI processing failed';
        const lowerErr = errMsg.toLowerCase();
        if (lowerErr.includes('rate limit') || lowerErr.includes('too many') || errMsg.includes('429')) {
          updateMsg('⏳ Too many requests right now. Please wait a minute and try again.');
        } else {
          updateMsg(`❌ ${errMsg}\n\n💡 Tip: Try with fewer or smaller files.`);
        }
      } finally { setLoading(false); }
      return;
    }

    // ── Standard path: no files attached → use /api/query/stream ──
    const body: any = { userId, sessionId: sessionIdRef.current, query: q, agentId: selectedAgent.id };
    if (activeCaseId) body.caseId = activeCaseId;
    if (ocrConfirmed && ocrText) body.context = { ocrText };
    // Inject database context if available
    if (getDbContext) {
      const dbCtx = getDbContext();
      if (dbCtx) {
        body.context = { ...(body.context || {}), dbContext: dbCtx };
      }
    }

    try {
      // Try streaming first
      let full = '';
      let gotError = false;

      try {
        const res = await fetch(`${apiUrl}/api/query/stream`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (!res.body) throw new Error('No response body');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamTimeout: ReturnType<typeof setTimeout> | null = null;
        let gotFirstByte = false;

        // Long window for the FIRST token (queries with memory/case context can be slow
        // to start), then a shorter gap between subsequent chunks.
        const FIRST_BYTE_MS = 60000;   // up to 60s for first token
        const INTER_CHUNK_MS = 25000;  // 25s between chunks once streaming
        const resetTimeout = () => {
          if (streamTimeout) clearTimeout(streamTimeout);
          streamTimeout = setTimeout(() => { try { reader.cancel(); } catch {} },
            gotFirstByte ? INTER_CHUNK_MS : FIRST_BYTE_MS);
        };
        resetTimeout();

        // Track case-orchestration progress/timing to show the user.
        const orchStart = Date.now();
        let caseInfo: { elapsedMs?: number; documentsUsed?: number; documentsTotal?: number } | null = null;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          resetTimeout(); // any activity (incl. status heartbeats) keeps the stream alive
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          let evt = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) evt = line.slice(7).trim();
            else if (line.startsWith('data: ')) {
              try {
                const d = JSON.parse(line.slice(6));
                if (evt === 'status') {
                  // Case micro-agent progress — show while no answer text yet.
                  if (!full && d.message) {
                    const secs = Math.round((Date.now() - orchStart) / 1000);
                    updateMsg('🔎 ' + d.message + (secs > 2 ? '  (' + secs + 's)' : ''));
                  }
                } else if (evt === 'caseinfo') {
                  caseInfo = d;
                } else if (evt === 'chunk' && d.text) {
                  gotFirstByte = true;
                  full += d.text;
                  updateMsg(full);
                } else if (evt === 'done') {
                  const charged = d?.billing?.tokensCharged || d?.billing?.tokens || 0;
                  setLastQueryTokens(charged);
                  setSessionTokens(prev => prev + charged);
                  loadTokenBalance();
                } else if (evt === 'error') {
                  gotError = true;
                  const errText = d.error || 'Stream error';
                  if (errText.toLowerCase().includes('invalid api key')) {
                    throw new Error('AI service temporarily unavailable');
                  }
                  throw new Error(errText);
                }
              } catch (parseErr: any) {
                if (gotError) throw parseErr;
              }
            }
          }
        }
        if (streamTimeout) clearTimeout(streamTimeout);
        // Append a completion note when the case micro-agent was involved.
        if (full && full.length > 0 && caseInfo && (caseInfo.documentsTotal || 0) > 0) {
          const secs = Math.round((caseInfo.elapsedMs || (Date.now() - orchStart)) / 1000);
          full = full + '\n\n_Answered from ' + (caseInfo.documentsUsed || 0) + ' of ' + (caseInfo.documentsTotal || 0) + ' case document(s) in ' + secs + 's._';
          updateMsg(full);
        }
      } catch (streamErr) {
        // If we already got text from the stream, don't fallback — use what we have
        if (full && full.length > 10) {
          console.log('Stream ended (timeout/cancel) but text received:', full.length, 'chars');
          // Token balance will be updated by CoreAPI's fallback billing
          loadTokenBalance();
          // Persist + fire onMessageComplete so conversation saves to memory/sidebar
          persistTurn(q, full);
          if (onMessageComplete) onMessageComplete(q, full);
          full = '__handled__'; // Mark as handled so it doesn't fire again below
        } else {
          // Streaming truly failed with no text — fallback to non-streaming endpoint
          console.warn('Streaming failed, trying non-streaming fallback:', streamErr);
          full = '';

        try {
          const fallbackRes = await fetch(`${apiUrl}/api/query`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          const data = await fallbackRes.json();
          if (data.success && data.response) {
            full = data.response;
            updateMsg(full);
            const charged = data.billing?.tokens || data.billing?.tokensCharged || 0;
            setLastQueryTokens(charged);
            setSessionTokens(prev => prev + charged);
            loadTokenBalance();
          } else {
            throw new Error(data.error || data.message || 'Query failed');
          }
        } catch (fallbackErr: any) {
          const errMsg = fallbackErr.message || 'Failed to get response.';
          let displayMsg = errMsg;
          try {
            if (errMsg.includes('{')) {
              const parsed = JSON.parse(errMsg.match(/\{.*\}/)?.[0] || '{}');
              displayMsg = parsed.error || errMsg;
            }
          } catch {}
          
          // Professional UX messages based on error type
          const lower = displayMsg.toLowerCase();
          const isBooting = lower.includes('starting up') || lower.includes('server starting');
          const isKeyError = lower.includes('invalid api key');
          // Rate limit can surface as "rate limit", "too many requests", or a raw 429.
          const isRateLimit = lower.includes('rate limit') || lower.includes('too many') || displayMsg.includes('429');
          const isTimeout = lower.includes('timed out') || lower.includes('timeout');
          const isFailed = lower.includes('failed to process');

          if (isRateLimit) {
            // Do NOT auto-retry — retrying during a rate-limit window makes it worse.
            updateMsg('⏳ Too many requests right now. Please wait a minute and try again.');
            full = 'error';
          } else if (isBooting || isKeyError || isFailed) {
            updateMsg('🔄 System is booting up... Please wait a moment while we initialize the AI service.\n\nThis usually takes 10-30 seconds. Your query will be processed automatically.');
            // Auto-retry after 5 seconds
            full = 'retrying';
            setTimeout(async () => {
              try {
                const retryRes = await fetch(`${apiUrl}/api/query`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(body),
                });
                const retryData = await retryRes.json();
                if (retryData.success && retryData.response) {
                  updateMsg(retryData.response);
                } else {
                  updateMsg('⏳ The AI system is still starting up. Please try again in a few seconds.\n\n💡 Tip: The system auto-recovers within 30-60 seconds after a restart.');
                }
              } catch {
                updateMsg('⏳ The AI system is still starting up. Please try again in a few seconds.\n\n💡 Tip: The system auto-recovers within 30-60 seconds after a restart.');
              }
            }, 5000);
          } else if (isTimeout) {
            updateMsg('⏱️ Request timed out. The server might be under heavy load. Please try again.');
          } else {
            updateMsg('🔄 System is initializing... Please wait a moment.\n\nYour query will be processed shortly.');
            full = 'retrying';
            setTimeout(async () => {
              try {
                const retryRes = await fetch(`${apiUrl}/api/query`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(body),
                });
                const retryData = await retryRes.json();
                if (retryData.success && retryData.response) {
                  updateMsg(retryData.response);
                } else {
                  updateMsg('⏳ System is still starting. Please try again in a moment.');
                }
              } catch {
                updateMsg('⏳ System is still starting. Please try again in a moment.');
              }
            }, 5000);
          }
          if (full !== 'retrying') full = 'error';
        }
        } // close else (no text received)
      }

      if (!full) updateMsg('🔄 System is booting up... The AI service is initializing. Please try again in a few seconds.');
      else if (full !== '__handled__' && full !== 'retrying' && full !== 'error') {
        persistTurn(q, full);
        if (onMessageComplete) onMessageComplete(q, full);
      }
    } catch (e: any) {
      const errMsg = e.message || '';
      if (errMsg.toLowerCase().includes('invalid api key') || errMsg.toLowerCase().includes('failed to process')) {
        updateMsg('🔄 System is booting up... Please wait a moment while we initialize the AI service.\n\nThis usually takes 10-30 seconds.');
      } else {
        updateMsg(`🔄 System is initializing... Please try again in a moment.\n\n${errMsg ? '(' + errMsg.slice(0, 100) + ')' : ''}`);
      }
    } finally { setLoading(false); }
  };

  const clearChat = () => {
    setMessages([]); handleClearOcr(); setSessionTokens(0); setLastQueryTokens(0);
    setShowTemplatePopup(false); setTemplateFilterText('');
  };

  // ── Send template-generated prompt ──
  const handleTemplateSend = async (prompt: string) => {
    if (!prompt.trim() || !selectedAgent || loading) return;
    // Temporarily set input to trigger handleSend logic
    const q = prompt;
    const userMsgId = 'user-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
    const assistantMsgId = 'bot-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);

    setMessages(prev => [
      ...prev,
      { id: userMsgId, role: 'user' as const, content: '📝 Template: ' + q.split('\n')[0].slice(0, 80) + '...', timestamp: new Date() },
      { id: assistantMsgId, role: 'assistant' as const, content: '', timestamp: new Date() },
    ]);
    setInput(''); setLoading(true); setLastQueryTokens(0);

    const updateMsg = (text: string) => setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, content: text } : m));

    const body: any = { userId, sessionId: sessionIdRef.current, query: q, agentId: selectedAgent.id };
    if (getDbContext) {
      const dbCtx = getDbContext();
      if (dbCtx) {
        body.context = { dbContext: dbCtx };
      }
    }

    try {
      let full = '';
      try {
        const res = await fetch(`${apiUrl}/api/query/stream`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.body) throw new Error('No response body');
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          let evt = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) evt = line.slice(7).trim();
            else if (line.startsWith('data: ')) {
              try {
                const d = JSON.parse(line.slice(6));
                if (evt === 'chunk' && d.text) { full += d.text; updateMsg(full); }
                else if (evt === 'done') {
                  const charged = d?.billing?.tokensCharged || d?.billing?.tokens || 0;
                  setLastQueryTokens(charged);
                  setSessionTokens(prev => prev + charged);
                  loadTokenBalance();
                } else if (evt === 'error') { throw new Error(d.error || 'Stream error'); }
              } catch (pe: any) { if (pe.message.includes('Stream') || pe.message.includes('error')) throw pe; }
            }
          }
        }
      } catch (streamErr) {
        if (!full || full.length <= 10) {
          // Fallback to non-streaming
          try {
            const fallbackRes = await fetch(`${apiUrl}/api/query`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body),
            });
            const data = await fallbackRes.json();
            if (data.success && data.response) {
              full = data.response;
              updateMsg(full);
              const charged = data.billing?.tokens || data.billing?.tokensCharged || 0;
              setLastQueryTokens(charged); setSessionTokens(prev => prev + charged); loadTokenBalance();
            } else { throw new Error(data.error || 'Query failed'); }
          } catch (fbErr: any) {
            updateMsg('🔄 System is initializing... Please try again in a moment.');
            full = 'error';
          }
        }
      }
      if (!full) updateMsg('🔄 No response received. Please try again.');
      else if (full !== 'error') { persistTurn(q, full); if (onMessageComplete) onMessageComplete(q, full); }
    } catch (e: any) {
      updateMsg('🔄 System is initializing... Please try again in a moment.');
    } finally { setLoading(false); }
  };

  // ── Render ──
  return (
    <div className="flex overflow-hidden bg-gray-50 rounded-xl border border-gray-200" style={{ height }}>
      {/* Sidebar */}
      {showSidebar && (
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col overflow-hidden flex-shrink-0">
          <div className="p-3 border-b border-gray-100">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Agents</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {agents.map(agent => (
              <div key={agent.id} onClick={() => handleSelectAgent(agent)}
                className={`p-2.5 rounded-xl cursor-pointer transition-all ${
                  selectedAgent?.id === agent.id
                    ? 'bg-gradient-to-r from-primary/10 to-secondary/5 border-l-4 border-primary shadow-sm'
                    : 'hover:bg-gray-50 border-l-4 border-transparent'
                }`}>
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-base flex-shrink-0">{agent.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-xs truncate">{agent.name}</div>
                    <div className="text-[10px] text-gray-500 truncate">{agent.description}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mt-1.5 ml-[46px]">
                  {agent.capabilities.slice(0, 3).map((c, i) => (
                    <span key={i} className="text-[8px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">{c}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {/* Token balance */}
          <div className="p-3 border-t border-gray-100 bg-gray-50/50">
            <div className="flex items-center gap-1.5 mb-1">
              <Zap size={12} className="text-amber-500" />
              <span className="text-[10px] font-semibold text-gray-600">Token Balance</span>
            </div>
            <div className="text-base font-bold text-gray-900">
              {tokensRemaining !== null ? tokensRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—'}
            </div>
          </div>
        </div>
      )}

      {/* Main chat area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedAgent ? (
          <>
            {/* Agent bar */}
            {showHeader && (
              <div className="bg-white border-b border-gray-200 px-4 py-2.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-xl shadow-md">{selectedAgent.icon}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">{title || selectedAgent.name}</span>
                        <span className="w-2 h-2 rounded-full bg-green-500" />
                        {voiceActive && <span className="text-[9px] px-2 py-0.5 bg-green-100 text-green-700 rounded-full font-medium animate-pulse">🎤 Voice</span>}
                      </div>
                      <div className="text-[10px] text-gray-500 max-w-xs truncate">{selectedAgent.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="hidden md:flex items-center gap-1 mr-2">
                      {selectedAgent.capabilities.slice(0, 5).map((c, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 bg-indigo-50 text-indigo-600 rounded-full font-medium">
                          {CAPABILITY_ICONS[c.toLowerCase()] || '✨'} {c}
                        </span>
                      ))}
                    </div>
                    {hasVoice && (
                      <button onClick={() => setVoiceActive(!voiceActive)}
                        className={`p-2 rounded-lg transition-all ${voiceActive ? 'bg-red-500 text-white' : 'bg-green-50 text-green-600 hover:bg-green-100'}`}>
                        {voiceActive ? <MicOff size={16} /> : <Mic size={16} />}
                      </button>
                    )}
                    {hasOcr && (
                      <button onClick={() => fileInputRef.current?.click()} className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100">
                        <ScanText size={16} />
                      </button>
                    )}
                    <button onClick={clearChat} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                {/* Token line */}
                <div className="flex items-center gap-3 mt-1.5 pt-1.5 border-t border-gray-100 text-[10px] text-gray-500">
                  <span className="flex items-center gap-1"><Zap size={10} className="text-amber-500" /> Session: <strong className="text-gray-700">{sessionTokens > 0 ? sessionTokens.toFixed(4) : '0'}</strong></span>
                  {lastQueryTokens > 0 && <span>Last: <strong className="text-indigo-600">{lastQueryTokens.toFixed(4)}</strong></span>}
                  <span className="flex-1" />
                  {tokensRemaining !== null && (
                    <span className={`font-medium ${tokensRemaining < 1 ? 'text-red-500' : 'text-green-600'}`}>
                      {tokensRemaining.toLocaleString('en-IN', { maximumFractionDigits: 2 })} remaining
                    </span>
                  )}
                </div>
                {/* Agent selector chips — shown when sidebar is hidden and multiple agents */}
                {!showSidebar && agents.length > 1 && (
                  <div className="flex gap-2 mt-2 pt-2 border-t border-gray-100 overflow-x-auto">
                    {agents.map(agent => (
                      <button
                        key={agent.id}
                        onClick={() => handleSelectAgent(agent)}
                        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
                          selectedAgent?.id === agent.id
                            ? 'border-primary bg-primary/5 text-primary'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-md bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-[10px]">{agent.icon}</span>
                        {agent.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Voice bar */}
            {voiceActive && hasVoice && (
              <GeminiVoiceChat agent={selectedAgent} sessionId={sessionIdRef.current} compact
                initialContext={messages
                  .filter(m => m.content && m.content.trim())
                  .slice(-12)
                  .map(m => `${m.role === 'user' ? 'Human' : 'Agent'}: ${m.content.replace(/^🎤 |^🔊 /, '').slice(0, 200)}`)
                  .join('\n')}
                onAgentTranscript={handleVoiceAgentTranscript} onUserTranscript={handleVoiceUserTranscript} onTurnComplete={handleVoiceTurnComplete} />
            )}

            {/* OCR (legacy) */}
            {hasOcr && !ocrConfirmed && (ocrProcessing || ocrText) && (
              <div className="px-4 py-3 bg-indigo-50/50 border-b border-indigo-100">
                {ocrProcessing && (
                  <div className="space-y-2 py-2">
                    {/* Progress bar */}
                    <div className="flex items-center gap-2">
                      <Loader2 className="animate-spin text-indigo-500" size={14} />
                      <div className="flex-1">
                        <div className="w-full bg-indigo-100 rounded-full h-2">
                          <div className="bg-indigo-500 h-2 rounded-full transition-all duration-300" style={{ width: `${ocrProgress || 5}%` }} />
                        </div>
                      </div>
                      <span className="text-[10px] font-mono text-indigo-600 min-w-[32px]">{ocrProgress}%</span>
                    </div>
                    {/* Page info + snippet */}
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] text-indigo-600">
                        {ocrTotalPages > 0 ? `Page ${ocrCurrentPage}/${ocrTotalPages}` : 'Analyzing document...'}
                        {ocrSnippet && <span className="ml-2 text-gray-500 italic">"{ocrSnippet}"</span>}
                      </span>
                      {ocrEstTime && <span className="text-[10px] text-gray-400">{ocrEstTime}</span>}
                    </div>
                  </div>
                )}
                {ocrText && !ocrConfirmed && (
                  <div className="space-y-2">
                    {/* Side-by-side: Original PDF + Extracted Text */}
                    <div className="flex items-center gap-2 mb-1">
                      <FileText size={12} className="text-indigo-500" />
                      <span className="text-[10px] font-semibold text-indigo-700">Verify extracted text against original</span>
                      {ocrTotalPages > 0 && <span className="text-[9px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded">{ocrTotalPages} pages</span>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-[200px]">
                      {/* Left: Original document preview */}
                      <div className="border rounded-lg bg-white overflow-hidden">
                        <div className="text-[9px] font-medium text-gray-500 px-2 py-1 bg-gray-50 border-b">Original Document</div>
                        {ocrPreview ? (
                          <img src={ocrPreview} alt="Original" className="w-full max-h-[160px] object-contain p-1" />
                        ) : ocrPdfFile ? (
                          <iframe
                            src={`data:application/pdf;base64,${ocrPdfFile}#toolbar=0&navpanes=0`}
                            className="w-full h-[160px] border-0"
                            title="PDF Preview"
                          />
                        ) : (
                          <div className="flex items-center justify-center h-[160px] text-gray-400 text-[10px]">No preview</div>
                        )}
                      </div>
                      {/* Right: Extracted text (editable) */}
                      <div className="border rounded-lg bg-white overflow-hidden">
                        <div className="text-[9px] font-medium text-gray-500 px-2 py-1 bg-gray-50 border-b">Extracted Text (editable)</div>
                        <textarea
                          value={ocrText}
                          onChange={e => setOcrText(e.target.value)}
                          className="w-full h-[160px] p-2 text-[10px] font-mono resize-none focus:outline-none focus:ring-1 focus:ring-indigo-300 border-0"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-gray-500 flex-1">Review & edit if needed, then confirm to use as context.</span>
                      <button onClick={handleConfirmOcr} className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-lg text-[10px] font-medium"><CheckCircle size={12} /> Confirm</button>
                      <button onClick={() => { setOcrText(null); setOcrPreview(null); setOcrAnalysis(null); setOcrPdfFile(null); }} className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 rounded-lg text-[10px] font-medium"><XCircle size={12} /> Reject</button>
                    </div>
                  </div>
                )}
              </div>
            )}
            {ocrConfirmed && (
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border-b border-green-200 text-xs text-green-800">
                <FileText size={14} className="text-green-600" />
                <span className="flex-1 font-medium">Document loaded — {ocrText?.length.toLocaleString()} chars</span>
                <button onClick={handleClearOcr} className="text-red-500 text-[10px]">✕ Clear</button>
              </div>
            )}

            {/* DocAI Attached Files Strip */}
            {attachedFiles.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-b border-blue-200 overflow-x-auto">
                <FileText size={14} className="text-blue-600 flex-shrink-0" />
                <div className="flex gap-2 flex-1 overflow-x-auto">
                  {attachedFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-1.5 px-2 py-1 bg-white border border-blue-200 rounded-lg text-[10px] flex-shrink-0">
                      {f.preview ? (
                        <img src={f.preview} alt="" className="w-6 h-6 rounded object-cover" />
                      ) : (
                        <FileText size={12} className="text-blue-500" />
                      )}
                      <span className="text-gray-700 font-medium max-w-[100px] truncate">{f.name}</span>
                      <button onClick={() => handleRemoveFile(i)} className="text-red-400 hover:text-red-600 ml-0.5">
                        <XCircle size={10} />
                      </button>
                    </div>
                  ))}
                </div>
                <span className="text-[9px] text-blue-600 font-medium flex-shrink-0">{attachedFiles.length}/10</span>
                <button onClick={handleClearFiles} className="text-red-500 text-[9px] flex-shrink-0 hover:underline">Clear all</button>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 bg-gray-50/50">
              {messages.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center max-w-xs">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-3xl mx-auto mb-3 shadow-lg">{selectedAgent.icon}</div>
                    <h3 className="text-base font-bold text-gray-900 mb-1">Chat with {selectedAgent.name}</h3>
                    <p className="text-xs text-gray-500 mb-3">{selectedAgent.description}</p>
                    <div className="flex flex-wrap justify-center gap-1.5 mb-3">
                      {selectedAgent.capabilities.map((c, i) => (
                        <span key={i} className="text-[9px] px-2 py-1 bg-white border border-gray-200 text-gray-600 rounded-full shadow-sm">
                          {CAPABILITY_ICONS[c.toLowerCase()] || '✨'} {c}
                        </span>
                      ))}
                    </div>
                    <div className="text-[10px] text-gray-400 space-y-1">
                      {hasVoice && <p>🎤 Voice available — click mic above</p>}
                      {hasOcr && <p>📄 OCR available — click 📄 above</p>}
                      <p>Type a question below to start</p>
                    </div>
                  </div>
                </div>
              )}
              {messages.map(msg => <ChatMessage key={msg.id} message={msg} />)}
              {loading && (
                <div className="flex gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
                  <div className="bg-white shadow-sm border border-gray-200 rounded-2xl px-3 py-2">
                    <div className="flex gap-1 mb-1">
                      <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <div className="w-1.5 h-1.5 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <LoadingMessage />
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-white border-t border-gray-200 px-4 py-3">
              <div className="flex gap-2 items-end relative">
                {/* Template Command Popup */}
                {showTemplatePopup && (
                  <TemplateCommandPopup
                    filterText={templateFilterText}
                    onGenerate={(prompt) => {
                      setShowTemplatePopup(false);
                      setTemplateFilterText('');
                      setInput('');
                      // Send the generated prompt directly
                      handleTemplateSend(prompt);
                    }}
                    onClose={() => {
                      setShowTemplatePopup(false);
                      setTemplateFilterText('');
                      setInput('');
                    }}
                  />
                )}
                <div className="flex-1 relative">
                  <input type="text" value={input} onChange={e => {
                    const val = e.target.value;
                    setInput(val);
                    // Detect slash command
                    if (val.startsWith('/')) {
                      setShowTemplatePopup(true);
                      setTemplateFilterText(val.slice(1)); // everything after "/"
                    } else {
                      setShowTemplatePopup(false);
                      setTemplateFilterText('');
                    }
                  }}
                    onKeyPress={e => { if (e.key === 'Enter' && !showTemplatePopup) handleSend(); }}
                    placeholder={attachedFiles.length > 0 ? 'Ask about the attached documents...' : ocrConfirmed ? 'Ask about the document...' : voiceActive ? 'Type or speak...' : `Ask ${selectedAgent.name}... (type / for templates)`}
                    className="w-full px-3 py-2.5 pr-10 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-primary text-sm"
                    disabled={loading} />
                  {hasOcr && (
                    <button onClick={() => fileInputRef.current?.click()} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-indigo-500"><Upload size={16} /></button>
                  )}
                </div>
                {hasVoice && (
                  <button onClick={() => setVoiceActive(!voiceActive)}
                    className={`p-2.5 rounded-xl border-2 ${voiceActive ? 'border-red-300 text-red-500 bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
                    {voiceActive ? <MicOff size={18} /> : <Mic size={18} />}
                  </button>
                )}
                <button onClick={handleSend} disabled={loading || !input.trim()}
                  className="px-5 py-2.5 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-semibold hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
                </button>
              </div>
              <div className="flex justify-between mt-1.5 px-0.5 text-[9px] text-gray-400">
                <span>{sessionTokens > 0 ? `⚡ ${sessionTokens.toFixed(4)} tokens` : '⚡ Token usage appears here'}</span>
                <span>{voiceActive ? '🎤 Voice + Text' : '💬 Text'} • {selectedAgent.name}</span>
              </div>
            </div>
            <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" multiple className="hidden" onChange={e => { const files = e.target.files; if (files) { Array.from(files).forEach(f => handleFileUpload(f)); } e.target.value = ''; }} />
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <div className="text-5xl mb-3">👈</div>
              <h3 className="text-lg font-semibold text-gray-600">Select an Agent</h3>
              <p className="text-xs">Choose from the sidebar to start</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentChatWidget;
